//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState } from 'react';
import { Avatar, makeStyles, DialogTitle, DialogContent, DialogActions, Dialog } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { setClassNameInDefns } from '../../common/definitionHelper';
import { LayoutContext, AppContext } from '../../common/contexts';
import { getNameByUser } from '../../common/textHelper';
// import { PLACEHOLDER_FIELD_TO } from '../../common/placeholderHelper';
import useRequiredFields from '../../common/hooks/useRequiredFields';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { makeMemberOwner } from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdAutocomplete from '../../common/elemental/AfitdAutocomplete';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {
        // minWidth: '540px',
        // width:'800px',
        // [theme.breakpoints.up('md')]: {
        // },
        '& .MuiDialog-paper': {
            [theme.breakpoints.up('md')]: {
                minWidth: '600px',
            },
        },
    },
    fieldContainerGroup: {
        display: 'flex',
        // margin: '10px 0',
        width: '800px',
        height: 'auto',
        alignItems: 'center',
        [theme.breakpoints.up('md')]: {
            minWidth: '540px',
        },
    },
    fieldContainer: { padding: '0 5px' },
    // fieldLabel: { fontWeight: 'bold', margin: '10px 0' },
    fieldLabel: { padding: '5px 0', marginTop: 20, fontWeight: 'bold' },
    subjectLimit: { marginTop: 10, color: 'grey' },
    messageField: { [theme.breakpoints.up('md')]: { minWidth: 600 } },
    buttonsContainer: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', margin: 16 },
    titleContainer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 'bold' },
    attachmentContainer: { display: 'flex', alignItems: 'center', marginRight: 5 },
    closeIcon: { cursor: 'pointer' },
    cancelButton: { marginRight: 16 },
    attachmentIcon: { cursor: 'pointer', border: '2px solid #F0F2F6', padding: 5, borderRadius: 50, color: 'rgba(17, 30, 20, 0.4)', fontSize: 35 },
    attachmentIconContainer: { padding: 0, marginRight: 16 },

    innerContainer: { padding: 20, border: '1px solid #D5D7DD', borderRadius: 4, marginTop: 10, background: 'rgba(255,255,255,0.9)' },
    fileTitle: { fontWeight: 'bold' },
    fileSize: { color: 'rgba(17, 30, 20, 0.4)' },
    inline: { display: 'flex', alignItems: 'center' },
    fileTextContainer: { marginLeft: 20 },
    recipientRowContainer: { display: 'flex', alignItems: 'center' },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        props,
        classes,
        isDirty,
        handleChangeMemberToReplace,
        handleChangeMessage,
        handleCloseDialog,
        handleClickSendButton,
        handleChangeAttachment,
        cancelModal,
        setCancelModal,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        getFieldError,
        selectedMember,
        members,
        setSelectedMember,
        currentOwner,
        handleOnConfirmReplace,
        confirmModalOpen,
        setConfirmModalOpen,
    } = params;

    const { open, onClose } = props;

    const sendButtonDisabled = !isDirty || !selectedMember || selectedMember?.email === currentOwner?.email;

    const defns = {
        root: { open, maxWidth: 'lg', disableBackdropClick: true, disableEscapeKeyDown: true },
        sendButton: {
            onClick: handleClickSendButton,
            label: 'Replace',
            color: 'primary',
            disabled: sendButtonDisabled,
        },
        cancelButton: { onClick: handleCloseDialog, label: 'Cancel' },
        fieldLabel: { variant: 'body1' },
        subjectLimit: { variant: 'body2', align: 'right' },
        attachmentText: { variant: 'body1' },
        messageField: { placeholder: 'Type your message...', multiline: true, rows: 3, rowsMax: 5, onChange: handleChangeMessage, error: getFieldError('message') },
        closeIcon: { onClick: handleCloseDialog },
        attachmentInput: { type: 'file', hidden: true, onChange: handleChangeAttachment, accept: '.jpg, .png' },
        attachmentIconContainer: { component: 'label' },
        memberToReplaceAdmin: {
            renderOption: option => <RecipientRow option={option} />,
            options: members?.filter(el => !el?.isOwner),
            value: selectedMember,
            getOptionLabel: option => getNameByUser(option),
            renderInput: params => <AfitdTextField {...params} placeholder="" color="secondary" />,
            onChange: handleChangeMemberToReplace,
            error: getFieldError('selectedMember'),
        },

        confirmCancelDialog: {
            title: 'Cancel',
            open: cancelModal,
            onConfirm: () => {
                setCancelModal(false);
                onClose();
                setSelectedMember({});
                // cleanUpAndClose();
            },
            onClose: () => setCancelModal(false),
        },
        confirmRelpaceDialog: {
            message: 'Ownership will be transferred but you will still be an Admin',
            yesButtonLabel: 'Confirm',
            noButtonLabel: 'Cancel',
            yesButtonColor: 'primary',
            title: 'Are you sure you want to replace Ownership?',
            open: confirmModalOpen,
            onConfirm: () => handleOnConfirmReplace(),
            onClose: () => setConfirmModalOpen(false),
        },

        pageErrorDialog: {
            message: 'You must enter all required fields.',
            open: pageErrorDialogOpen,
            onClose: () => setPageErrorDialogOpen(false),
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const RecipientRow = props => {
    const classes = useStyles();
    const { option } = props;
    const { profilePicUrl, firstName, lastName } = option;
    return (
        <div className={classes.recipientRowContainer}>
            <div>
                <Avatar src={profilePicUrl} />
            </div>
            &nbsp;&nbsp;
            <div>
                {firstName} {lastName}
            </div>
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SendMessageDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const { setSnackbarErrorMessage, setSnackbarMessage } = useContext(LayoutContext);
    const { employerId, currentEmployer } = useContext(AppContext);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onClose, open } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [cancelModal, setCancelModal] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const { /*invalidRequiredFields, setInvalidRequiredFields,*/ getFieldError, clearInvalidRequiredField } = useRequiredFields();

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const members = _get(currentEmployer, 'members');

    //---------------------------------------------------------------------
    // Engage check
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    const currentOwner = members?.find(el => el.isOwner);

    React.useEffect(() => {
        // setSelectedMember(currentOwner);
        setIsDirty(false);
    }, [currentOwner, members, open]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleChangeMemberToReplace = (evt, arr) => {
        setSelectedMember(arr);
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField('selectedMember');
    };

    const handleCloseDialog = () => {
        if (isDirty && !selectedMember && selectedMember?.email !== currentOwner?.email) setCancelModal(true);
        else onClose();
    };

    const handleClickSendButton = async () => {
        if (selectedMember) {
            setConfirmModalOpen(true);
        }
    };

    const handleOnConfirmReplace = async () => {
        if (selectedMember) {
            setConfirmModalOpen(false);
            const memberName = `${selectedMember?.firstName} ${selectedMember?.lastName}`;
            const updatedMembers = members?.map(el => {
                if (el?.email === selectedMember.email) {
                    return { ...el, isAdmin: true, isOwner: true };
                } else return { ...el, isOwner: false };
            });
            makeMemberOwner({ updatedMembers, employerId, setSnackbarErrorMessage, setSnackbarMessage, memberName });
            onClose();
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        props,
        classes,
        isDirty,
        handleChangeMemberToReplace,
        handleCloseDialog,
        handleClickSendButton,
        cancelModal,
        setCancelModal,
        getFieldError,
        selectedMember,
        members,
        setSelectedMember,
        currentOwner,
        handleOnConfirmReplace,
        confirmModalOpen,
        setConfirmModalOpen,
        // setIsOkToSave,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            <Dialog {...defns.root}>
                <DialogTitle>
                    <div {...defns.titleContainer}>
                        Replace Owner / Admin
                        <CloseIcon {...defns.closeIcon} />
                    </div>
                </DialogTitle>
                <DialogContent>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>Select Member to make Owner: *</AfitdTypography>
                        <AfitdAutocomplete {...defns.memberToReplaceAdmin} />
                    </div>
                </DialogContent>
                <DialogActions>
                    <div {...defns.buttonsContainer}>
                        <div>
                            <AfitdButton {...defns.cancelButton} />
                            <AfitdButton {...defns.sendButton} />
                        </div>
                    </div>
                </DialogActions>
            </Dialog>
            <AfitdConfirmDialog {...defns.confirmCancelDialog} />
            <AfitdConfirmDialog {...defns.confirmRelpaceDialog} />
        </>
    );
};

export default SendMessageDialog;
